import { useCallback, useEffect, useState } from 'react';
import { leadgenApi, type LeadGenDashboardResponse } from '@/api/leadgen.api';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { PageSection } from '@/components/ui/PageSection';
import { ChartCard } from '@/components/ui/ChartCard';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { showApiError } from '@/utils/apiError';

export function LeadGenDashboardPage() {
  const [data, setData] = useState<LeadGenDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setData(await leadgenApi.getDashboard());
    } catch (err) {
      showApiError(err, 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="Lead Gen"
        title="Lead Gen Dashboard"
        description="Visitor tracking, forms, scoring, routing, and CRM conversion"
      >
        <DashboardSkeleton kpiCount={4} />
      </DashboardLayout>
    );
  }

  if (!data) return null;

  return (
    <DashboardLayout
      moduleLabel="Lead Gen"
      title="Lead Gen Dashboard"
      description="Live metrics from visitor tracking, forms, scoring, routing, and CRM conversion"
    >
      <KpiGrid columns={4}>
        <StatCard
          title="Total visitors"
          value={data.totalVisitors.toLocaleString()}
          icon={<PeopleIcon />}
          trend={`CVR ${data.conversionRate}% · ${data.formSubmissions.toLocaleString()} submissions`}
        />
        <StatCard title="Total leads" value={data.totalLeads.toLocaleString()} icon={<CampaignIcon />} />
        <StatCard title="Conversions" value={data.conversions.toLocaleString()} icon={<ShowChartIcon />} />
        <StatCard title="Avg. lead score" value={`${data.avgLeadScore} pts`} icon={<TimelineIcon />} />
      </KpiGrid>

      <div className="grid gap-6 lg:grid-cols-3">
        <ChartCard title="Inbound acquisition funnel" className="lg:col-span-2">
          <div className="space-y-3">
            {data.funnel.map((f, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-between text-xs font-medium text-slate-700 dark:text-slate-300">
                  <span>{f.stage}</span>
                  <span className="tabular-nums">{f.count.toLocaleString()} ({f.percent})</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                  <div className="h-full rounded-full bg-primary-600" style={{ width: f.percent }} />
                </div>
              </div>
            ))}
          </div>
        </ChartCard>

        <section className="card p-5">
          <h3 className="section-title">Top lead sources</h3>
          <div className="mt-4 space-y-3">
            {data.topSources.length === 0 && (
              <p className="text-sm text-slate-500">No lead source data yet.</p>
            )}
            {data.topSources.map((ts, idx) => (
              <div key={idx} className="flex items-center justify-between border-b border-slate-100 py-2 text-sm last:border-0 dark:border-slate-800">
                <div>
                  <p className="font-medium text-slate-800 dark:text-slate-200">{ts.source}</p>
                  <p className="text-xs text-slate-500">{ts.leads} leads</p>
                </div>
                <span className="text-xs text-slate-500">Share: {ts.conversionRate}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <PageSection title="Lead score distribution">
          <div className="card flex flex-wrap gap-3 p-4">
            {data.scoreDistribution.map((g) => (
              <div key={g.grade} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 dark:border-slate-700 dark:bg-slate-800/50">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{g.grade}</span>
                <span className="ml-2 text-xs text-slate-500">{g.count}</span>
              </div>
            ))}
          </div>
        </PageSection>

        <PageSection title="Campaign ROI">
          <div className="card divide-y divide-slate-100 dark:divide-slate-800">
            {data.campaignPerformance.map((c) => (
              <div key={c.id} className="flex justify-between px-4 py-3 text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-300">{c.name}</span>
                <span className="font-semibold text-success-700">{c.roiPercentage}% ROI</span>
              </div>
            ))}
          </div>
        </PageSection>
      </div>
    </DashboardLayout>
  );
}
